import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { lang } from "config"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import SelectSearchable from "components/forms/select-searchable"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import { getBranchList } from "helpers/general-helper"

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function isIntLike(v) {
  const n = num(v)
  return Math.abs(n - Math.round(n)) < 1e-9
}

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : null,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

/**
 * Normalize gr_items dari response API.
 *
 * Untuk edit route: backend sudah menyertakan qty_selected yang di-merge
 * dari return items — tinggal pakai langsung.
 *
 * Untuk create route (pickGR): qty_selected dari GR = 0 karena belum ada return.
 */
function normalizeGRItems(items = []) {
  return (Array.isArray(items) ? items : []).map((x) => ({
    id: x.id || x.receipt_item_id,
    product_unit_id: x.product_unit_id,
    product_unit:
      x.product_unit?.name ||
      x.product_unit?.product_unit_name ||
      x.product_unit_name ||
      x.product_name ||
      "-",
    qty_received: num(x.qty_received),
    batch_no: x.batch_no || "",
    expired_date: x.expired_date ? String(x.expired_date).slice(0, 10) : "",
    qty_returnable: Math.max(0, num(x.qty_received) - num(x.qty_returned)),
    checked: num(x.qty_selected) > 0,
    qty_selected: num(x.qty_selected) || 0,
    unit_cost: x.unit_cost ?? x?.po_item?.cost_price ?? "",
    notes: x.notes ?? "",
  }))
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PurchaseReturnForm() {
  const { id } = useParams()
  const isEditRoute = Boolean(id)

  const { can, currentBranch } = useAuth()
  const { setTitle } = useOutletContext()
  const location = useLocation()
  const navigate = useNavigate()

  const branchSelection = useMemo(() => getBranchList(), [])
  const branchIdList = useMemo(
    () => (branchSelection ? branchSelection.map((x) => x.id).filter(Boolean) : []),
    [branchSelection]
  )

  // ── Header state ──────────────────────────────────────────────────────────
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [returnDate, setReturnDate] = useState(todayISO())
  const [reason, setReason] = useState("")
  const [status, setStatus] = useState("DRAFT")
  const [returnId, setReturnId] = useState(id || null)

  // ── Selected GR ───────────────────────────────────────────────────────────
  const [selectedGR, setSelectedGR] = useState(null)
  const receiptId = selectedGR?.id ?? null

  // ── GR items ──────────────────────────────────────────────────────────────
  const [grItems, setGrItems] = useState([])

  // ── UI ────────────────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({})
  const [acting, setActing] = useState(false)

  // ── GR picker modal ───────────────────────────────────────────────────────
  const [grModalOpen, setGrModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [searchParam, setSearchParam] = useState("")
  const searchDebounceRef = useRef(null)
  const pickReqRef = useRef(0)
  const [initialLoading, setInitialLoading] = useState(isEditRoute)

  const isDraft = String(status || "").toUpperCase() === "DRAFT"
  const isLocked = !isDraft

  // ── Load existing return (edit route) ─────────────────────────────────────
  //
  // Response yang dikonfirmasi dari API:
  // {
  //   data: {
  //     return: { id, receipt_id, outlet_id, status, return_date, reason, items: [...] },
  //     gr:     { id, gr_number, po_id, receipt_date, ... },
  //     items:  [...],   ← return items (sama dengan return.items)
  //     gr_items: [      ← GR items + qty_selected sudah di-merge backend ✅
  //       { id, product_unit_id, product_unit, qty_received, qty_selected, unit_cost, ... }
  //     ]
  //   }
  // }
  //
  // Fix utama: HANYA pakai payload.gr_items, TIDAK fetch /goods-receipts/:id lagi.
  // Fetch ulang GR items itulah yang menyebabkan grItems di-overwrite checked:false
  // di production karena async race condition.
  // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      if (!isEditRoute) {
        setInitialLoading(false)
        return
      }

      const run = async () => {
        try {
          setInitialLoading(true)
          nProgress.start()

          const res = await apiFetch(`/purchase-returns/${id}`)
          const payload = res?.data || res
          const ret = payload?.return || payload

          setReturnId(ret?.id ?? id)
          setStatus(ret?.status ?? "DRAFT")
          if (ret?.outlet_id != null) setOutletId(ret.outlet_id)
          if (ret?.return_date) setReturnDate(String(ret.return_date).slice(0, 10))
          if (ret?.reason != null) setReason(ret.reason || "")

          const gr = payload?.gr || ret?.goods_receipt || null
          if (gr) {
            setSelectedGR(gr)
          } else if (ret?.receipt_id) {
            setSelectedGR({ id: ret.receipt_id })
          }

          const grItemsRaw = payload?.gr_items
          if (Array.isArray(grItemsRaw) && grItemsRaw.length > 0) {
            setGrItems(normalizeGRItems(grItemsRaw))
            return
          }

          const returnItems = Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(ret?.items)
            ? ret.items
            : []

          const receiptIdFallback = ret?.receipt_id ?? gr?.id ?? null

          if (receiptIdFallback) {
            try {
              const resGR = await apiFetch(`/goods-receipts/${receiptIdFallback}`)
              const payloadGR = resGR?.data || resGR
              const itemsGR = payloadGR?.items || payloadGR?.receipt_items || []

              if (gr == null) {
                setSelectedGR((prev) => ({ ...payloadGR, ...(prev || {}) }))
              }

              const draftMap = new Map()
              for (const it of returnItems) {
                const rid = String(it.receipt_item_id || "")
                if (!rid) continue

                draftMap.set(rid, {
                  qty_selected: num(it.qty_returned),
                  unit_cost: it.unit_cost ?? "",
                  notes: it.notes ?? "",
                })
              }

              setGrItems(
                (Array.isArray(itemsGR) ? itemsGR : []).map((x) => {
                  const rid = String(x.id || x.receipt_item_id || "")
                  const draft = draftMap.get(rid)

                  return {
                    id: x.id || x.receipt_item_id,
                    product_unit_id: x.product_unit_id,
                    product_unit:
                      x.product_unit?.name || x.product_unit_name || x.product_name || "-",
                    qty_received: num(x.qty_received),
                    batch_no: x.batch_no || "",
                    expired_date: x.expired_date ? String(x.expired_date).slice(0, 10) : "",
                    qty_returnable: x.qty_received - (x?.qty_returned ?? 0) ?? 0,
                    checked: draft ? num(draft.qty_selected) > 0 : false,
                    qty_selected: draft ? num(draft.qty_selected) : 0,
                    unit_cost: draft?.unit_cost ?? x.unit_cost ?? x?.po_item?.cost_price ?? "",
                    notes: draft?.notes ?? x.notes ?? "",
                  }
                })
              )
            } catch (e) {
              toast.error(e?.message || "Gagal load GR items")
            }
          }
        } catch (e) {
          toast.error(e?.message || "Gagal load purchase return")
        } finally {
          setInitialLoading(false)
          nProgress.done()
        }
      }

      run()
    }, [isEditRoute, id])

  // ── GR List (modal) ───────────────────────────────────────────────────────
  const grQuery = useMemo(() => {
    if (!outletId) return null
    const oid = outletId?.id ?? outletId
    return {
      outlet_id: oid,
      branch_list: JSON.stringify(branchIdList),
      page,
      per_page: 5,
      search: searchParam || undefined,
      status: "POSTED",
      po_type: "REGULAR",
    }
  }, [outletId, branchIdList, page, searchParam])

  const { data: gr_result_set, isLoading: isLoadingGR } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `goods-receipts`,
    grQuery
  )

  useEffect(() => {
    if (isLoadingGR) nProgress.start()
    else nProgress.done()
  }, [isLoadingGR])

  const grList = useMemo(() => {
    const arr = gr_result_set?.data?.data
    return Array.isArray(arr) ? arr : []
  }, [gr_result_set])

  const grMeta = gr_result_set?.data || {}
  const canPrev = Boolean(grMeta?.prev_page_url)
  const canNext = Boolean(grMeta?.next_page_url)

  const onSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setSearchParam(String(val || ""))
      setPage(1)
    }, 300)
  }

  // ── Pick GR from modal (create route) ─────────────────────────────────────
  const pickGR = async (gr) => {
    const reqId = ++pickReqRef.current

    setSelectedGR(gr)
    setGrModalOpen(false)
    setGrItems([])

    try {
      nProgress.start()
      const res = await apiFetch(`/goods-receipts/${gr.id}`)
      if (reqId !== pickReqRef.current) return // abaikan response lama

      const payload = res?.data || res
      const items = payload?.items || payload?.receipt_items || []

      if (!Array.isArray(items) || items.length === 0) {
        toast.error(`GR ${gr.gr_number || gr.id} tidak memiliki items.`)
        return
      }

      // GR baru dipilih untuk create: qty_selected = 0, checked = false
      setGrItems(normalizeGRItems(items))
    } catch (e) {
      toast.error(e?.message || "Gagal load GR items")
    } finally {
      nProgress.done()
    }
  }

  // ── UI helpers ────────────────────────────────────────────────────────────
  const toggleItem = (receipt_item_id, checked) => {
    setGrItems((prev) =>
      prev.map((r) => {
        if (String(r.id) !== String(receipt_item_id)) return r

        const maxQty = num(r.qty_returnable ?? r.qty_received)
        const current = num(r.qty_selected)
        const safeQty = Math.max(0, Math.min(current || 1, maxQty))

        return {
          ...r,
          checked,
          qty_selected: checked ? safeQty : 0,
        }
      })
    )
  }

  const updateItem = (receipt_item_id, patch) => {
    setGrItems((prev) =>
      prev.map((r) => (String(r.id) === String(receipt_item_id) ? { ...r, ...patch } : r))
    )
  }

  const selectedCount = useMemo(
    () => grItems.filter((x) => x.checked && num(x.qty_selected) > 0).length,
    [grItems]
  )
  const selectedQtyTotal = useMemo(
    () => grItems.reduce((acc, x) => acc + (x.checked ? num(x.qty_selected) : 0), 0),
    [grItems]
  )

  // ── Validation ────────────────────────────────────────────────────────────
  const validateBeforeCreate = () => {
    const e = {}
    if (!outletId) e.outlet_id = "Outlet " + lang.field_required
    if (!receiptId) e.receipt_id = "Goods Receipt " + lang.field_required
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const buildItemsPayload = () => {
    const picked = grItems
      .filter((x) => x.checked && num(x.qty_selected) > 0)
      .map((x) => ({
        receipt_item_id: x.id,
        qty_returned: num(x.qty_selected),
        unit_cost: x.unit_cost === "" || x.unit_cost == null ? null : num(x.unit_cost),
        notes: x.notes || null,
      }))
    return { items: picked }
  }

  const validateBeforeSaveDraft = (payload) => {
    const e = {}
    if (!receiptId) e.receipt_id = "Goods Receipt " + lang.field_required

    const items = Array.isArray(payload?.items) ? payload.items : []
    if (!items.length) e.items = "Pilih minimal 1 product untuk direturn"

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const q = num(it.qty_returned)

      if (!it.receipt_item_id) {
        e[`items.${i}.receipt_item_id`] = "receipt_item_id wajib"
        continue
      }

      const source = grItems.find((x) => String(x.id) === String(it.receipt_item_id))
      const maxQty = num(source?.qty_returnable ?? source?.qty_received)

      if (q <= 0) e[`items.${i}.qty_returned`] = "Qty harus > 0"
      else if (!isIntLike(q)) e[`items.${i}.qty_returned`] = "Qty harus bilangan bulat"
      else if (q > maxQty) {
        e[`items.${i}.qty_returned`] = `Qty return tidak boleh melebihi ${maxQty}`
      }
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ── Actions ───────────────────────────────────────────────────────────────
  const createReturnFull = async () => {
    if (!validateBeforeCreate()) return
    const payloadItems = buildItemsPayload()
    if (!validateBeforeSaveDraft(payloadItems)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Membuat return...", { id: "pr-create" })

      const res = await apiFetch(`/goods-receipts/${receiptId}/purchase-returns/create-full`, {
        method: "POST",
        body: {
          outlet_id: outletId?.id ?? outletId,
          return_date: returnDate,
          reason: reason || null,
          items: payloadItems.items,
        },
      })

      const data = res?.data || res
      const newId = data?.return?.id || data?.id

      toast.success(res?.message || "Return berhasil dibuat", { id: "pr-create" })

      if (newId) {
        setReturnId(newId)
        setStatus(data?.return?.status || "DRAFT")

        if (Array.isArray(data?.gr_items) && data.gr_items.length > 0) {
          setGrItems(normalizeGRItems(data.gr_items))
        }

        if (!isEditRoute) {
          navigate(`/return`, {
            replace: true,
            state: { back: "Purchase Returns", from: location.pathname, transition: "slide" },
          })
        }
      }
    } catch (e) {
      toast.error(e?.message || "Gagal membuat return", { id: "pr-create" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const saveDraft = async () => {
    const payload = buildItemsPayload()
    if (!validateBeforeSaveDraft(payload)) return
    if (isLocked) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Saving draft...", { id: "pr-save" })

      const res = await apiFetch(`/purchase-returns/${returnId}/items`, {
        method: "POST",
        body: payload,
      })

      toast.success(res?.message || "Draft tersimpan", { id: "pr-save" })
    } catch (e) {
      toast.error(e?.message || "Gagal save draft", { id: "pr-save" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const postReturn = async () => {
    if (!returnId) return
    if (!can?.("return-order.post")) return
    if (isLocked) return
    if (!window.confirm(`POST Return ${returnId}?`)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Posting...", { id: "pr-post" })

      const res = await apiFetch(`/purchase-returns/${returnId}/post`, { method: "POST" })
      const data = res?.data || res

      toast.success(res?.message || "Return posted", { id: "pr-post" })
      setStatus(data?.status || "POSTED")
    } catch (e) {
      toast.error(e?.message || "Gagal post return", { id: "pr-post" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const cancelReturn = async () => {
    if (!returnId) return
    if (!can?.("purchase-return.cancel")) return
    if (String(status).toUpperCase() === "CANCELLED") return

    const r = window.prompt("Alasan cancel (optional):", "")
    if (r === null) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Cancelling...", { id: "pr-cancel" })

      const res = await apiFetch(`/purchase-returns/${returnId}/cancel`, {
        method: "POST",
        body: r ? { reason: r } : null,
      })
      const data = res?.data || res

      toast.success(res?.message || "Return cancelled", { id: "pr-cancel" })
      setStatus(data?.status || "CANCELLED")
    } catch (e) {
      toast.error(e?.message || "Gagal cancel return", { id: "pr-cancel" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-sm text-neutral-500">Loading purchase return...</div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Transition type="fade">
      <div className="pb-8 space-y-6">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">
              {returnId ? "Edit Purchase Return" : "Create Purchase Return"}
              <span className="ml-2 text-xs font-medium text-neutral-500">
                (Outlet → GR POSTED → pilih product)
              </span>
            </h1>
            <p className="text-xs text-neutral-500">
              Qty return harus bilangan bulat. Posting akan mengurangi stok dan menulis stock_transaction.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/return", { state: location.state })}
              className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            >
              Back
            </button>

            {can?.("return-order.cancel") && String(status).toUpperCase() === "DRAFT" && (
              <button
                type="button"
                disabled={acting}
                onClick={cancelReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500 disabled:opacity-60"
              >
                {acting ? "..." : "Cancel"}
              </button>
            )}

            {can?.("return-order.post") && returnId && String(status).toUpperCase() === "DRAFT" && (
              <button
                type="button"
                disabled={acting}
                onClick={postReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-green-700 rounded-xl hover:bg-green-600 disabled:opacity-60"
              >
                {acting ? "..." : "Post"}
              </button>
            )}

            {(can?.("return-order.create") || can?.("return-order.update")) && returnId && (
              <button
                type="button"
                disabled={acting || isLocked}
                onClick={saveDraft}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  isLocked
                    ? "bg-neutral-400 cursor-not-allowed"
                    : "bg-neutral-900 hover:bg-neutral-800"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Save Draft"}
              </button>
            )}

            {(can?.("return-order.create") || can?.("return-order.update")) && !returnId && (
              <button
                type="button"
                disabled={acting || !outletId || !receiptId}
                onClick={createReturnFull}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  outletId && receiptId
                    ? "bg-neutral-900 hover:bg-neutral-800"
                    : "bg-neutral-400 cursor-not-allowed"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Create Return"}
              </button>
            )}
          </div>
        </div>

        {/* ── Header form ─────────────────────────────────────────────────── */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="px-4 py-5 sm:p-6 grid grid-cols-2 gap-4">

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={Boolean(returnId)}
                filter={(value) => value.name}
                onChange={(value) => {
                  const nextOutletId = value?.id ?? value ?? null

                  setOutletId(nextOutletId)

                  // edit route: abaikan reset apa pun
                  if (isEditRoute) return

                  setSelectedGR(null)
                  setGrItems([])
                  setErrors((prev) => ({ ...prev, receipt_id: undefined, items: undefined }))
                  setPage(1)
                  setSearchParam("")
                }}
                selection={branchSelection}
                isLoading={false}
                value={outletId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder="-- Select Outlet --"
              />
              <ErrorMessage error={errors?.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Return Date</label>
              <input
                type="date"
                disabled={Boolean(returnId)}
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">
                Goods Receipt (POSTED)<sup className="text-xs text-red-700"> *</sup>
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    disabled
                    value={
                      selectedGR
                        ? `${selectedGR?.gr_number || selectedGR?.id} - ${
                            selectedGR?.purchase_order?.po_number || selectedGR?.po_id || "-"
                          }`
                        : ""
                    }
                    placeholder={outletId ? "Pilih GR..." : "Pilih outlet dulu..."}
                  />
                </div>
                <button
                  type="button"
                  disabled={!outletId || Boolean(returnId)}
                  onClick={() => setGrModalOpen(true)}
                  className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                    outletId && !returnId
                      ? "bg-neutral-900 hover:bg-neutral-800"
                      : "bg-neutral-400 cursor-not-allowed"
                  }`}
                >
                  Pilih GR
                </button>
              </div>
              <ErrorMessage error={errors?.receipt_id} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={Boolean(returnId)}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="Alasan return (optional)"
              />
              <ErrorMessage error={errors?.header} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

          </div>
        </div>

        {/* ── Items section ────────────────────────────────────────────────── */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="px-4 py-5 sm:p-6 space-y-3">

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Pilih Product Return</div>
                <div className="text-xs text-neutral-500">
                  Checklist produk dari GR, isi qty_returned (integer).
                </div>
              </div>
              <div className="text-xs text-neutral-600">
                Selected: <b>{selectedCount}</b> | Qty Total: <b>{selectedQtyTotal}</b>
              </div>
            </div>

            <ErrorMessage error={errors?.items} />

            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Pick</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Product Unit</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Received</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Batch</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Expired</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Returned</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Unit Cost</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Notes</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-neutral-200">
                  {!receiptId && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-xs text-center text-neutral-500">
                        Pilih outlet lalu pilih GR untuk memunculkan item.
                      </td>
                    </tr>
                  )}

                  {receiptId && grItems.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-xs text-center text-neutral-500">
                        Item GR belum termuat. Pastikan endpoint{" "}
                        <b>GET /goods-receipts/{receiptId}</b> mengembalikan <b>items</b>.
                      </td>
                    </tr>
                  )}

                  {grItems.map((it) => (
                    <tr key={it.id}>
                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="checkbox"
                          disabled={isLocked}
                          checked={Boolean(it.checked)}
                          onChange={(e) => toggleItem(it.id, e.target.checked)}
                        />
                      </td>

                      <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                        {it.product_unit}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.qty_received ?? "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.batch_no || "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.expired_date || "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          max={it?.qty_returnable ?? it?.qty_received}
                          disabled={isLocked || !it.checked}
                          value={it.checked ? it.qty_selected : 0}
                          onChange={(e) => {
                            const raw = num(e.target.value)
                            const maxQty = num(it.qty_returnable ?? it.qty_received)
                            const nextQty = Math.max(0, Math.min(raw, maxQty))
                            updateItem(it.id, { qty_selected: nextQty })
                          }}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        <div className="mt-1 text-[11px] text-neutral-500">
                          Max: {num(it.qty_returnable ?? it.qty_received)}
                        </div>
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          // disabled={isLocked || !it.checked}
                          disabled={true}
                          value={it.unit_cost ?? ""}
                          onChange={(e) => updateItem(it.id, { unit_cost: e.target.value })}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700">
                        <input
                          disabled={isLocked || !it.checked}
                          value={it.notes ?? ""}
                          onChange={(e) => updateItem(it.id, { notes: e.target.value })}
                          className="w-64 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-neutral-500">
              * Validasi maksimal returnable + stok dilakukan saat <b>Post</b> oleh backend service.
            </div>
          </div>
        </div>

      </div>

      {/* ── GR picker modal ───────────────────────────────────────────────── */}
      {grModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-end p-4">
          <div
            className="absolute inset-0 z-[9998] bg-black/40"
            onClick={() => setGrModalOpen(false)}
          />
          <div className="relative z-[9999] w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">

            <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Pilih Goods Receipt (POSTED)</div>
                <div className="text-xs text-neutral-500">
                  Ter-filter outlet_id = {outletId?.id ?? outletId}.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setGrModalOpen(false)}
                className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="relative">
                <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    strokeWidth={1}
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx={10} cy={10} r={7} />
                    <line x1={21} y1={21} x2={15} y2={15} />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={`${lang.search} Goods Receipt`}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>

              <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">GR No</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Date</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-neutral-200">
                    {isLoadingGR && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-xs text-center text-neutral-500">
                          {lang.loading_data}
                        </td>
                      </tr>
                    )}

                    {!isLoadingGR && grList.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-xs text-center text-neutral-500">
                          {lang.no_data}
                        </td>
                      </tr>
                    )}

                    {!isLoadingGR &&
                      grList.map((gr) => (
                        <tr
                          key={gr.id}
                          onClick={() => pickGR(gr)}
                          className="cursor-pointer hover:bg-neutral-50"
                        >
                          <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                            {gr.gr_number ?? gr.id}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.po_number ?? gr.po_id ?? "-"}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.supplier_name ?? gr.supplier_name_snapshot ?? gr.supplier_id ?? "-"}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.receipt_date ?? gr.created_at ?? "-"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">
                  Page: <b>{page}</b>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={!canPrev}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={!canNext}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-neutral-500">
                * List otomatis filter: outlet_id + status POSTED.
              </div>
            </div>
          </div>
        </div>
      )}
    </Transition>
  )
}